/* eslint-disable react/jsx-props-no-spreading */

import { createSelector } from '@reduxjs/toolkit';
import { HTMLAttributes, ReactNode } from 'preact/compat';
import { useDispatch } from 'react-redux';

import { cn } from '@/lib/utils';
import { AppState, useSelector, useStore } from '@/redux/helper';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';

import { setMembersOnlyWarningModalState } from '../MembersOnlyWarningModal/redux/actions';
import { setProductsPromptModalState } from '../products/ProductsPromptModal/redux/actions';
import { createSelectProductsPromptModalState } from '../products/ProductsPromptModal/redux/selectors';
import { buildHabitatURL } from './utils';

export interface HabitatLinkProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  habitatId: string;
  slug: string;
  zooSlug: string;
  query?: string;
  productsPromptModalState?: Parameters<typeof setProductsPromptModalState>[0];
  children: ReactNode | ((props: { restricted: boolean }) => ReactNode);
}

type RestrictedHabitatLinkProps = Omit<HabitatLinkProps, 'children'> & {
  children: ReactNode;
};
type AccessibleHabitatLinkProps = Omit<RestrictedHabitatLinkProps, 'habitatId' | 'productsPromptModalState'>;

const AccessibleHabitatLink = ({ slug, zooSlug, query, children, className, ...rest }: AccessibleHabitatLinkProps) => (
  <a href={buildHabitatURL(zooSlug, slug, query)} className={cn('preflight preflight-a', className)} {...rest}>
    {children}
  </a>
);

const selectProfileImage = createSelector(
  (state: AppState) => state.allHabitats,
  (_: AppState, id: string) => id,
  (allHabitats, id) => allHabitats.find((h) => h._id === id)?.profileImage,
);

const RestrictedHabitatLink = ({
  habitatId,
  slug,
  zooSlug,
  children,
  onClick,
  productsPromptModalState,
  ...rest
}: RestrictedHabitatLinkProps) => {
  const dispatch = useDispatch();
  const store = useStore();
  const logged = useSelector((state) => state.user.logged);
  return (
    <AccessibleHabitatLink
      slug={slug}
      zooSlug={zooSlug}
      {...rest}
      data-native
      onClick={(e) => {
        e.preventDefault();
        if (logged) {
          const defaultState = createSelectProductsPromptModalState(habitatId)(store.getState());
          const state: typeof productsPromptModalState = {
            ...defaultState,
            ...productsPromptModalState,
            entryPoint: 'habitat-link',
          };
          dispatch(setProductsPromptModalState(state));
        } else {
          dispatch(
            setMembersOnlyWarningModalState({
              habitat: {
                id: habitatId,
                slug,
                zooSlug,
              },
              type: 'habitat',
              imageUrl: selectProfileImage(store.getState(), habitatId),
            }),
          );
        }
        onClick?.(e);
      }}
    >
      {children}
    </AccessibleHabitatLink>
  );
};

const HabitatLink = ({ habitatId, children, productsPromptModalState, ...rest }: HabitatLinkProps) => {
  const canAccessHabitat = useSelector(createSelectCanAccessHabitat(habitatId));
  return canAccessHabitat ? (
    <AccessibleHabitatLink {...rest}>
      {typeof children === 'function' ? children({ restricted: false }) : children}
    </AccessibleHabitatLink>
  ) : (
    <RestrictedHabitatLink habitatId={habitatId} productsPromptModalState={productsPromptModalState} {...rest}>
      {typeof children === 'function' ? children({ restricted: true }) : children}
    </RestrictedHabitatLink>
  );
};

export default HabitatLink;
